import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { generateReusedLabel } from '@shared/utils/reusedLabelGenerator';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ReceiveApiAdapter } from '../../adapters/ReceiveApiAdapter';
import { useCarts, useCouriers, useReceiveFlow, useReceiveProductLocations } from '../../hooks';
import { useLocationOrders } from '../../hooks/useLocationOrders';
import { useProcessedOrders } from '../../hooks/useProcessedOrders';
import { useReceivedLines } from '../../hooks/useReceivedLines';
import { useToast } from '@shared/hooks/use-toast';
import { IncidenceType, RejectionReason } from '../../models';
import { Breadcrumb, CartsList, KPICards, OrderProductsList, ReceivedLinesList } from '../display';
import {
  CourierSelector,
  CreateLocationOrderModal,
  CreateLocationOrderModalRef,
  FinalizeReceptionModal,
  FinalizeReceptionModalRef,
  IncidenceModal,
  IncidenceModalRef,
  LocationOrdersModal,
  LocationOrdersModalRef,
  ProcessedOrdersModal,
  ProcessedOrdersModalRef,
  ScannerInput,
} from '../interaction';

export const Main: React.FC = () => {
  const { t } = useTranslation('reusedModule');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const receptionId = id ? parseInt(id, 10) : 0;
  const { toast } = useToast();

  // Refs de modales
  const incidenceModalRef = useRef<IncidenceModalRef>(null);
  const processedOrdersModalRef = useRef<ProcessedOrdersModalRef>(null);
  const locationOrdersModalRef = useRef<LocationOrdersModalRef>(null);
  const createLocationOrderModalRef = useRef<CreateLocationOrderModalRef>(null);
  const finalizeReceptionModalRef = useRef<FinalizeReceptionModalRef>(null);

  // Hook para obtener líneas recibidas
  const { data: receivedLinesData, isLoading: isLoadingReceivedLines } = useReceivedLines(
    receptionId,
    { per_page: 100 },
    receptionId > 0,
  );

  // Hook para obtener órdenes de ubicación
  const { data: locationOrdersData } = useLocationOrders(receptionId, { per_page: 100 }, receptionId > 0);

  // Hook para obtener pedidos procesados
  const { data: processedOrdersData } = useProcessedOrders(receptionId, { per_page: 1 }, receptionId > 0);

  // Hook para obtener carritos utilizados
  const { data: cartsData, isLoading: isLoadingCarts } = useCarts(receptionId, receptionId > 0);

  const {
    reception,
    isLoadingReception,
    receptionError,
    currentStep,
    validatedCart,
    selectedCourierId,
    identifiedOrder,
    scannedQuantities,
    selectedBatchIds,
    isProcessing,
    isReceiving,
    error,
    isOrderComplete,
    hasMissingProducts,
    selectCourier,
    processScan,
    receiveOrder,
    clearError,
    clearIdentifiedOrder,
    goBack,
    handleBatchChange,
  } = useReceiveFlow(receptionId);

  const { locationsByProductId, isLoadingLocations } = useReceiveProductLocations(identifiedOrder);

  const { data: couriers, isLoading: isLoadingCouriers } = useCouriers();

  // Mutación para crear orden de ubicación
  const queryClient = useQueryClient();
  const httpClient = createHttpClient(apiClientWH);
  const receiveAdapter = new ReceiveApiAdapter(httpClient);

  const createLocationOrderMutation = useMutation({
    mutationFn: ({ cartId, notes }: { cartId: number; notes?: string }) =>
      receiveAdapter.createLocationOrder(receptionId, { cart_id: cartId, notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['receptionReceivedLines', receptionId] });
      queryClient.invalidateQueries({ queryKey: ['receptionLocationOrders', receptionId] });
      queryClient.invalidateQueries({ queryKey: ['reception', receptionId] });
      queryClient.invalidateQueries({ queryKey: ['receptionCarts', receptionId] });

      toast({
        title: t('toasts.success', 'Éxito'),
        description: t('toasts.location_order.success', 'Orden de ubicación creada exitosamente'),
      });

      goBack();
    },
    onError: (error: any) => {
      console.error('Error creating location order:', error);
      toast({
        title: t('toasts.error', 'Error'),
        description:
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          t('toasts.location_order.error', 'Error al crear la orden de ubicación'),
        variant: 'destructive',
      });
    },
  });

  // Auto-ocultar errores del flow después de 5 segundos (si existen fuera del toast)
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        clearError();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, clearError]);

  // Mutation para finalizar recepción
  const finalizeReceptionMutation = useMutation({
    mutationFn: () => {
      const httpClient = createHttpClient(apiClientWH);
      const receiveAdapter = new ReceiveApiAdapter(httpClient);
      return receiveAdapter.finalizeReception(receptionId);
    },
    onSuccess: () => {
      toast({
        title: t('toasts.success', 'Éxito'),
        description: t('toasts.finalize.success', 'Recepción finalizada exitosamente'),
      });
      queryClient.invalidateQueries({ queryKey: ['reused-reception', receptionId] });
      queryClient.invalidateQueries({ queryKey: ['received-lines', receptionId] });
      queryClient.invalidateQueries({ queryKey: ['processed-orders', receptionId] });
      queryClient.invalidateQueries({ queryKey: ['location-orders', receptionId] });
      queryClient.invalidateQueries({ queryKey: ['receptionCarts', receptionId] });
    },
    onError: (error: any) => {
      let errorMsg = t('toasts.finalize.error', 'No se pudo finalizar la recepción. Por favor, inténtalo de nuevo.');

      if (error?.response?.data?.error?.error_code) {
        const errorCode = error.response.data.error.error_code;
        switch (errorCode) {
          case 'CANNOT_FINALIZE_NOT_ALL_ORDERS_PROCESSED':
            errorMsg = t(
              'toasts.finalize.not_all_processed',
              'No se pueden finalizar la recepción hasta que todos los pedidos estén procesados.',
            );
            break;
          case 'CANNOT_FINALIZE_WITHOUT_LOCATION_ORDER':
            errorMsg = t(
              'toasts.finalize.without_location_order',
              'No se puede finalizar la recepción. Todos los productos aceptados deben tener una orden de ubicación asignada. Por favor, crea las órdenes de ubicación pendientes.',
            );
            break;
          default:
            if (error?.response?.data?.error?.message) {
              errorMsg = error.response.data.error.message;
            }
        }
      } else if (error?.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error?.message) {
        errorMsg = error.message;
      }

      toast({
        title: t('toasts.error', 'Error'),
        description: errorMsg,
        variant: 'destructive',
      });
      console.error('Error al finalizar recepción:', error?.response?.data || error);
    },
  });

  // Validación del ID
  if (!receptionId || receptionId <= 0) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-4">
            {t('error.invalid_id', 'ID de recepción inválido')}
          </h1>
          <p className="text-muted-foreground mb-6">
            {t('error.invalid_id_desc', 'Por favor, accede a una recepción válida desde el listado.')}
          </p>
          <button
            onClick={() => navigate('/reused')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            {t('actions.back_to_list', 'Ir al listado')}
          </button>
        </div>
      </div>
    );
  }

  // Manejar clic en botón de recepcionar
  const handleReceiveClick = () => {
    if (isOrderComplete) {
      handleReceiveOrder();
    } else if (identifiedOrder) {
      incidenceModalRef.current?.open(identifiedOrder, scannedQuantities, handleReceiveWithModal);
    }
  };

  const handleReceiveOrder = async () => {
    const result = await receiveOrder();
    if (result.success) {
      toast({
        title: t('toasts.success', 'Éxito'),
        description: result.message,
      });
    } else {
      toast({
        title: t('toasts.error', 'Error'),
        description: result.message,
        variant: 'destructive',
      });
    }
  };

  const handleReceiveWithModal = async (data: {
    hasCourierIncidence: boolean;
    incidenceType: IncidenceType | null;
    rejectionEntriesByKey: Record<
      string | number,
      Array<{ reason: RejectionReason; quantity: number; images: File[] }>
    >;
  }) => {
    const result = await receiveOrder(data);
    if (result.success) {
      toast({
        title: t('toasts.success', 'Éxito'),
        description: result.message,
      });
    } else {
      toast({
        title: t('toasts.error', 'Error'),
        description: result.message,
        variant: 'destructive',
      });
    }
  };

  const handleOpenCreateLocationOrderModal = () => {
    if (receptionId && validatedCart) {
      createLocationOrderModalRef.current?.open(validatedCart, receptionId);
    }
  };

  const handleOpenFinalizeModal = () => {
    if (reception) {
      finalizeReceptionModalRef.current?.open(
        reception.code || `${t('breadcrumb.reception', 'Recepción')} #${receptionId}`,
        reception.total_orders,
        processedOrdersData?.meta?.total || 0,
        handleFinalizeReception,
      );
    }
  };

  const handleFinalizeReception = async () => {
    await finalizeReceptionMutation.mutateAsync();
  };

  const handlePrintLabel = async () => {
    if (!reception) return;
    const labelUrl = await generateReusedLabel({
      code: reception.code,
      date: new Date(reception.created_at.replace(' ', 'T')).toLocaleDateString('es-ES'),
    });
    window.open(labelUrl, '_blank');
  };

  const handleCreateLocationOrder = async (cartId: number, notes?: string) => {
    await createLocationOrderMutation.mutateAsync({ cartId, notes });
  };

  if (isLoadingReception) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">{t('loading.message', 'Cargando recepción...')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (receptionError) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-4">
            {t('error.title', 'Error al cargar la recepción')}
          </h1>
          <p className="text-muted-foreground mb-6">{receptionError}</p>
          <button
            onClick={() => navigate('/reused')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            {t('actions.back_to_list', 'Volver al listado')}
          </button>
        </div>
      </div>
    );
  }

  if (!reception) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-muted-foreground mb-4">
            {t('error.not_found', 'Recepción no encontrada')}
          </h1>
          <p className="text-muted-foreground mb-6">
            {t('error.not_found_desc', 'No se pudo encontrar la recepción con ID {{id}}.', { id: receptionId })}
          </p>
          <button
            onClick={() => navigate('/reused')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            {t('actions.back_to_list', 'Volver al listado')}
          </button>
        </div>
      </div>
    );
  }

  if (reception.status === 0) {
    return (
      <div className="container mx-auto p-4 max-w-6xl h-[calc(100vh-6rem)] flex flex-col">
        <Breadcrumb
          receptionId={receptionId}
          receptionCode={reception.code}
          receptionStatus={reception.status}
          onPrintClick={handlePrintLabel}
        />

        <div className="mb-6 bg-green-50 dark:bg-green-950/20 border-l-4 border-green-500 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <svg
                className="w-8 h-8 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h2 className="text-md font-bold text-green-900 dark:text-green-100 mb-2">
                {t('status.finalized', 'Recepción Finalizada')}
              </h2>
              <p className="text-green-800 dark:text-green-200">
                {t(
                  'status.finalized_desc',
                  'Esta recepción ha sido finalizada exitosamente. No se pueden procesar más pedidos ni realizar cambios.',
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row flex-1 min-h-0 gap-6">
          <div className="flex flex-col lg:w-1/2 space-y-4">
            <h2 className="text-lg font-semibold text-foreground">{t('summary.title', 'Resumen de la Recepción')}</h2>
            <KPICards
              totalOrders={reception?.total_orders || 0}
              processedOrders={processedOrdersData?.meta?.total || 0}
              locationOrdersCount={locationOrdersData?.meta?.total || 0}
              onOrdersClick={() => processedOrdersModalRef.current?.open(receptionId)}
              onLocationOrdersClick={() => locationOrdersModalRef.current?.open(receptionId)}
            />
            <div className="flex-shrink-0 mt-6">
              <CartsList carts={cartsData?.data || []} isLoading={isLoadingCarts} />
            </div>
          </div>

          <div className="flex flex-col lg:w-1/2 min-h-0">
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <h2 className="text-lg font-semibold text-foreground">
                {t('summary.received_products', 'Productos Recibidos')}
              </h2>
              <span className="text-sm text-muted-foreground">
                {receivedLinesData?.data?.length || 0} {t('summary.lines', 'línea')}
                {(receivedLinesData?.data?.length || 0) !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex-1 min-h-0">
              <ReceivedLinesList lines={receivedLinesData?.data || []} isLoading={isLoadingReceivedLines} />
            </div>
          </div>
        </div>
        <ProcessedOrdersModal ref={processedOrdersModalRef} />
        <LocationOrdersModal ref={locationOrdersModalRef} />
      </div>
    );
  }

  const getStepDescription = () => {
    switch (currentStep) {
      case 'cart':
        return t('steps.cart', 'Escanea el código del carrito para comenzar');
      case 'identify':
        return t('steps.identify', 'Selecciona el courier y escanea el código de barras del paquete');
      case 'lines':
        return t('steps.lines', 'Escanea los productos del pedido (opcional)');
      default:
        return '';
    }
  };

  const getScannerPlaceholder = () => {
    if (currentStep === 'cart') return t('scanner.placeholder_cart', 'Escanea o escribe el código del carrito...');
    if (currentStep === 'identify')
      return t('scanner.placeholder_package', 'Escanea o escribe el código de barras del paquete...');
    return t('scanner.placeholder_product', 'Escanea o escribe el código del producto...');
  };

  const isScannerDisabled = (currentStep === 'identify' && !selectedCourierId) || isProcessing;
  const processedOrdersCount = processedOrdersData?.meta?.total || 0;
  const totalOrdersCount = reception?.total_orders || 0;
  const hasOrderInProgress = currentStep === 'lines' || Boolean(identifiedOrder);
  const isFinalizeDisabled = hasOrderInProgress || totalOrdersCount === 0 || processedOrdersCount < totalOrdersCount;
  const finalizeTooltip = isFinalizeDisabled
    ? t(
        'actions.finalize_disabled_tooltip',
        hasOrderInProgress
          ? 'Debes terminar de recepcionar el pedido actual antes de finalizar'
          : 'Debes recepcionar todos los pedidos (incluyendo incidencias por faltantes) para finalizar',
      )
    : undefined;

  return (
    <div className="container mx-auto p-4 max-w-6xl h-auto lg:h-[calc(100vh-6rem)] flex flex-col">
      <Breadcrumb
        receptionId={receptionId}
        receptionCode={reception.code}
        receptionStatus={reception.status}
        onFinalizeClick={handleOpenFinalizeModal}
        onPrintClick={handlePrintLabel}
        isFinalizeDisabled={isFinalizeDisabled}
        finalizeTooltip={finalizeTooltip}
      />

      {currentStep === 'lines' && identifiedOrder && (
        <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border border-blue-200/50 dark:border-blue-800/30 rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                    />
                  </svg>
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                </div>
              </div>
              <div>
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider">
                    {t('card.receiving', 'Recibiendo')}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-foreground">{identifiedOrder.ref}</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {identifiedOrder.customer?.name}
                  {identifiedOrder.shipment?.tracking_number && (
                    <span className="ml-2 font-mono text-xs">• {identifiedOrder.shipment.tracking_number}</span>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={clearIdentifiedOrder}
              className="text-muted-foreground hover:text-foreground transition-colors p-2 rounded-full hover:bg-white/50 dark:hover:bg-gray-800/50"
              title={t('actions.cancel', 'Cancelar y volver')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-950/30 border-l-4 border-red-400 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row flex-1 lg:min-h-0 gap-6">
        <div className="flex flex-col lg:w-1/2 space-y-4">
          {validatedCart &&
            currentStep !== 'cart' &&
            (() => {
              const receptionProducts =
                validatedCart.stock_location_carts?.filter((slc) => slc.returned_reception_id === receptionId) || [];
              const totalProducts = receptionProducts.length;
              const totalQuantity = receptionProducts.reduce((sum, slc) => sum + slc.qty, 0);
              const canCreateLocationOrder = totalProducts > 0;

              return (
                <div className="bg-background border border-border rounded-lg overflow-hidden">
                  <div className="bg-green-50 dark:bg-green-950/20 border-b border-green-200 dark:border-green-800 px-4 py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="bg-green-100 dark:bg-green-900/40 rounded-lg p-2">
                          <svg
                            className="w-5 h-5 text-green-600 dark:text-green-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                            />
                          </svg>
                        </div>
                        <div>
                          <div className="text-xs text-green-600 dark:text-green-400 font-medium mb-0.5">
                            {t('card.active_cart', 'Carrito Activo')}
                          </div>
                          <div className="text-lg font-bold text-foreground font-mono">{validatedCart.code}</div>
                        </div>
                      </div>
                      {!identifiedOrder && (
                        <button
                          onClick={goBack}
                          className="px-3 py-1.5 text-sm font-medium text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/40 rounded-md transition-colors"
                        >
                          {t('actions.change', 'Cambiar')}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="px-4 py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <svg
                            className="w-4 h-4 text-muted-foreground"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                            />
                          </svg>
                          <span className="text-sm text-muted-foreground">
                            <span className="font-semibold text-foreground">{totalProducts}</span>{' '}
                            {t('card.product', 'producto')}
                            {totalProducts !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <svg
                            className="w-4 h-4 text-muted-foreground"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                            />
                          </svg>
                          <span className="text-sm text-muted-foreground">
                            <span className="font-semibold text-foreground">{totalQuantity}</span>{' '}
                            {t('card.units', 'unidades')}
                          </span>
                        </div>
                      </div>
                      {canCreateLocationOrder && !identifiedOrder && (
                        <button
                          onClick={handleOpenCreateLocationOrderModal}
                          className="px-4 py-2 text-sm font-medium bg-primary hover:bg-primary/90 text-white rounded-md transition-colors flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          {t('actions.create_location_order', 'Crear Orden de Ubicación')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

          {currentStep === 'cart' && (
            <div className="space-y-6">
              <ScannerInput
                stepDescription={getStepDescription()}
                placeholder={getScannerPlaceholder()}
                onScan={processScan}
                disabled={isProcessing}
                disabledReason={isProcessing ? t('scanner.reason_processing', 'Procesando...') : undefined}
                buttonText={t('actions.validate_cart', 'Validar Carrito')}
              />
              <CartsList carts={cartsData?.data || []} isLoading={isLoadingCarts} />
            </div>
          )}

          {currentStep === 'identify' && (
            <div className="space-y-6">
              <CourierSelector
                couriers={couriers ?? []}
                selectedCourierId={selectedCourierId}
                onSelect={selectCourier}
                isLoading={isLoadingCouriers}
              />
              <ScannerInput
                stepDescription={getStepDescription()}
                placeholder={getScannerPlaceholder()}
                onScan={processScan}
                disabled={isScannerDisabled}
                buttonText={t('actions.identify_order', 'Identificar Pedido')}
              />
            </div>
          )}

          {currentStep === 'lines' && (
            <div className="space-y-6">
              <ScannerInput
                stepDescription={getStepDescription()}
                placeholder={getScannerPlaceholder()}
                onScan={processScan}
                disabled={isProcessing || isReceiving}
                disabledReason={
                  isReceiving
                    ? t('actions.receiving', 'Recepcionando...')
                    : isProcessing
                      ? t('scanner.reason_processing', 'Procesando...')
                      : undefined
                }
                buttonText={t('actions.scan_product', 'Escanear Producto')}
              />
              <div className="bg-background border border-border rounded-lg p-6">
                <div className="space-y-4">
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      {t('actions.receive_order', 'Recepcionar Pedido')}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {isOrderComplete
                        ? t('card.complete', '✓ Todos los productos han sido escaneados')
                        : hasMissingProducts
                          ? t('card.missing', 'Hay productos pendientes por escanear')
                          : t('card.ready', 'Puedes recepcionar el pedido')}
                    </p>
                  </div>
                  <button
                    onClick={handleReceiveClick}
                    disabled={isReceiving}
                    className={`w-full py-4 px-6 rounded-lg font-semibold text-white transition-all duration-200 flex items-center justify-center gap-2 ${isOrderComplete ? 'bg-green-600 hover:bg-green-700' : 'bg-primary hover:bg-primary/90'} disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isReceiving ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        {t('actions.receiving', 'Recepcionando...')}
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {t('actions.receive_order', 'Recepcionar Pedido')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col lg:w-1/2 lg:min-h-0">
          {currentStep === 'lines' && identifiedOrder ? (
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between mb-4 flex-shrink-0">
                <h2 className="text-lg font-semibold text-foreground">
                  {t('summary.order_products', 'Productos del Pedido')}
                </h2>
                <span className="text-sm text-muted-foreground">
                  {identifiedOrder.lines?.length || 0} {t('summary.lines', 'línea')}
                  {(identifiedOrder.lines?.length || 0) !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex-1 lg:min-h-0 overflow-y-auto">
                <OrderProductsList
                  order={identifiedOrder}
                  scannedQuantities={scannedQuantities}
                  selectedBatchIds={selectedBatchIds}
                  onBatchChange={handleBatchChange}
                  locationsByProductId={locationsByProductId}
                  isLoadingLocations={isLoadingLocations}
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-col h-full space-y-6">
              <KPICards
                totalOrders={reception?.total_orders || 0}
                processedOrders={processedOrdersData?.meta?.total || 0}
                locationOrdersCount={locationOrdersData?.meta?.total || 0}
                onOrdersClick={() => processedOrdersModalRef.current?.open(receptionId)}
                onLocationOrdersClick={() => locationOrdersModalRef.current?.open(receptionId)}
              />
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-foreground">
                    {t('summary.received_products', 'Productos Recibidos')}
                  </h2>
                </div>
                <div className="flex-1 min-h-0">
                  <ReceivedLinesList lines={receivedLinesData?.data || []} isLoading={isLoadingReceivedLines} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <IncidenceModal ref={incidenceModalRef} />
      <ProcessedOrdersModal ref={processedOrdersModalRef} />
      <LocationOrdersModal ref={locationOrdersModalRef} />
      <CreateLocationOrderModal ref={createLocationOrderModalRef} onConfirm={handleCreateLocationOrder} />
      <FinalizeReceptionModal ref={finalizeReceptionModalRef} />
    </div>
  );
};
